import { useNavigate, useLocation } from 'react-router-dom';
import { ActionMenu, ActionList } from '@primer/react';
import styled from 'styled-components';
import { isDashboardPathname } from './platform-side';

type WorkspaceOption = {
  value: 'private' | 'public';
  label: string;
};

const workspaceOptions: WorkspaceOption[] = [
  { value: 'private', label: 'Private workspace' },
  { value: 'public', label: 'Public database' },
];

export function WorkspaceDropdown({ variant = 'light' }: { variant?: 'light' | 'dark' }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const currentWorkspaceValue = isDashboardPathname(pathname) ? 'private' : 'public';
  const currentOption = workspaceOptions.find(option => option.value === currentWorkspaceValue);

  const handleWorkspaceChange = (value: 'private' | 'public') => {
    if (value === currentWorkspaceValue) return; // Prevent navigation if already on the correct page

    if (value === 'public') {
      navigate('/');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <Wrapper>
      <ActionMenu>
        <ActionMenu.Button className={`workspace-button ${variant}`}>
          <WorkspaceText variant={variant}>{currentOption?.label}</WorkspaceText>
        </ActionMenu.Button>
        <ActionMenu.Overlay>
          <StyledActionList selectionVariant="single">
            {workspaceOptions.map(option => (
              <ActionList.Item
                key={option.value}
                onSelect={() => {
                  handleWorkspaceChange(option.value);
                }}
                selected={option.value === currentWorkspaceValue}
              >
                {option.label}
              </ActionList.Item>
            ))}
          </StyledActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .workspace-button {
    border: none !important;
    background-color: transparent !important;
    display: flex;
    align-items: center;
    padding: 0 16px 0 20px !important;
    margin-bottom: 1px;
    height: 40px;
    border-radius: 999px !important;
  }

  .workspace-button.light {
    color: var(--platform-gray-800);

    &:hover {
      background-color: var(--bgColor-neutral-muted) !important;
      color: var(--fgColor-default) !important;
      text-decoration: none !important;
    }
  }

  .workspace-button.dark {
    color: white;
  }
`;

const StyledActionList = styled(ActionList)`
  /* Remove extra padding/margins that cause hover spacing issues */

  [data-component='ActionList.Item'] {
    margin: 0;
    padding: var(--space__sm) var(--space__lg);
  }
`;

const WorkspaceText = styled.span<{ variant: 'light' | 'dark' }>`
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__bold);
  color: ${props => (props.variant === 'dark' ? 'white' : 'var(--platform-gray-800)')};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  transition: color 0.12s ease-in-out;
`;
